import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import InternalChatPanel from "@/components/app/InternalChatPanel";
import ThemeToggle from "@/components/app/ThemeToggle";
import ChangePasswordModal from "@/components/app/ChangePasswordModal";
import {
  WhatsappLogo, ChartLineUp, Package, Buildings, Kanban, ChatsCircle, SignOut, PlugsConnected, Robot, Users, Lightning, Clock, Storefront, Wallet, Gear, Eye, ShieldCheck, ChartBar, AddressBook, CheckCircle, Broadcast, Lifebuoy, Headset, Lightbulb, LockKey, Code,
} from "@phosphor-icons/react";

const NAV_MASTER = [
  { to: "/master", label: "Dashboard", icon: ChartLineUp },
  { to: "/master/plans", label: "Planos", icon: Package },
  { to: "/master/companies", label: "Empresas", icon: Buildings },
  { to: "/master/resellers", label: "Revendedores", icon: Storefront },
  { to: "/master/support", label: "Suporte", icon: Headset },
  { to: "/master/suggestions", label: "Sugestões", icon: Lightbulb },
  { to: "/master/site-settings", label: "Site", icon: Gear },
];

const NAV_RESELLER = [
  { to: "/reseller", label: "Dashboard", icon: ChartLineUp },
  { to: "/reseller/companies", label: "Empresas", icon: Buildings },
  { to: "/reseller/billing", label: "Créditos", icon: Wallet },
];

const NAV_COMPANY_STATIC = [
  { to: "/kanban", label: "Kanban", icon: Kanban, key: "kanban" },
  { to: "/chat", label: "Chat", icon: ChatsCircle, key: "chat" },
  { to: "/contacts", label: "Contatos", icon: AddressBook, key: "contacts" },
  { to: "/reports", label: "Relatórios", icon: ChartBar, key: "reports" },
  { to: "/ai", label: "IA", icon: Robot, key: "ai" },
  { to: "/chatbot", label: "Chatbot", icon: Robot, key: "chatbot" },
  { to: "/mass-sending", label: "Envio Massa", icon: Broadcast, key: "mass_sending" },
  { to: "/team", label: "Equipe", icon: Users, key: "team" },
  { to: "/work-schedules", label: "Jornada", icon: Clock, key: "work_schedules" },
  { to: "/templates", label: "Templates", icon: Lightning, key: "templates" },
  { to: "/business-hours", label: "Horário", icon: Clock, key: "business_hours" },
  { to: "/closure-reasons", label: "Motivos", icon: CheckCircle, key: "closure_reasons" },
  { to: "/anti-ban", label: "Anti-ban", icon: ShieldCheck, key: "anti_ban" },
  { to: "/connections", label: "Conexões", icon: PlugsConnected, key: "connections" },
  { to: "/general-settings", label: "Ajustes Gerais", icon: Gear, key: "general_settings" },
  { to: "/integrations", label: "API", icon: Code, key: "api_integration" },
  { to: "/help", label: "Ajuda", icon: Lifebuoy, key: "help" },
  { to: "/suggestions", label: "Sugestão", icon: Lightbulb, key: "suggestions" },
];

export default function AppLayout() {
  const { user, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const isMaster = user?.role === "master";
  const isReseller = user?.role === "reseller_owner";
  // Filter company nav by the user's permissions (returned from /auth/me).
  // For owners/master this comes back as "all keys"; for attendants it's a subset.
  const userPerms = new Set(user?.permissions || []);
  const navCompany = NAV_COMPANY_STATIC.filter((item) => userPerms.has(item.key));
  const nav = isMaster ? NAV_MASTER : (isReseller ? NAV_RESELLER : navCompany);
  const [logoUrl, setLogoUrl] = useState(null);
  const [showChangePwd, setShowChangePwd] = useState(false);

  useEffect(() => {
    if (user?.company_id) {
      api.get("/general-settings").then((r) => setLogoUrl(r.data.logo_url || null)).catch(() => {});
    }
  }, [user?.company_id]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen grid grid-cols-[220px_1fr] bg-[#F7F7F5] dark:bg-[#0a0a0a]">
      {/* Sidebar */}
      <aside className="bg-[#111111] text-[#F7F7F5] flex flex-col" data-testid="sidebar">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-9 w-9 object-contain" />
          ) : (
            <div className="h-9 w-9 bg-[#22A882] text-[#0A3629] flex items-center justify-center">
              <WhatsappLogo size={20} weight="bold" />
            </div>
          )}
          <div>
            <div className="font-bold tracking-tight text-sm">NETSZAP</div>
            <div className="text-[10px] uppercase tracking-widest text-[#a3b5ac]">
              {isMaster ? "Admin" : (isReseller ? (user?.reseller_name || "Revendedor") : (user?.company_name || "Empresa"))}
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to} to={to} end
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-[#22A882] text-[#0A3629] font-semibold"
                    : "text-[#c8d5cf] hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} weight="bold" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs font-semibold truncate">{user?.name}</div>
            <div className="text-[10px] text-[#a3b5ac] truncate">{user?.email}</div>
          </div>
          <button
            onClick={() => setShowChangePwd(true)}
            data-testid="nav-change-password"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#c8d5cf] hover:bg-white/5 hover:text-white transition-colors duration-200"
          >
            <LockKey size={18} weight="bold" />
            Alterar senha
          </button>
          <ThemeToggle />
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#c8d5cf] hover:bg-white/5 hover:text-white transition-colors duration-200"
          >
            <SignOut size={18} weight="bold" />
            Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="overflow-x-hidden dark:bg-[#0a0a0a]">
        {isImpersonating && (
          <div className="bg-[#D97706] text-white px-4 py-2 flex items-center justify-between text-sm" data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
              <Eye size={16} weight="fill" />
              <span className="font-semibold">
                Você está logado como <b>{user?.name}</b> ({user?.company_name || user?.reseller_name || user?.role}). Feche esta aba pra voltar ao Master.
              </span>
            </div>
            <button
              onClick={() => window.close()}
              data-testid="impersonation-close-btn"
              className="text-xs underline hover:no-underline"
            >
              Fechar aba
            </button>
          </div>
        )}
        <Outlet />
      </main>
      <InternalChatPanel />
      <ChangePasswordModal open={showChangePwd} onClose={() => setShowChangePwd(false)} />
    </div>
  );
}
